import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { invalidateReplyQueueMembership } from '@/lib/crm/reply-queue-cache';

/**
 * Trash/Restore change `os_conversation_threads.deleted_at`, which the
 * Reply Queue's membership now depends on (F1's `excludeSoftDeletedThreads`)
 * — without this, the badge and rows cache stay stale until the next 60s
 * poll (F3). Both queue caches invalidate together so they can't drift
 * apart from each other, matching the invalidation this file already does
 * for `conversation-threads`/`thread-counts`.
 */
async function invalidateThreadLifecycleCaches(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['conversation-threads'] }),
    queryClient.invalidateQueries({ queryKey: ['thread-counts'] }),
    invalidateReplyQueueMembership(queryClient),
  ]);
}

export const useSoftDeleteThreads = (): ReturnType<
  typeof useMutation<{ deleted: number }, Error, { threadIds: string[] }>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadIds }) => {
      const { error, count } = await supabase
        .from('os_conversation_threads')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', threadIds);

      if (error) throw error;

      return { deleted: count ?? 0 };
    },
    onSuccess: () => invalidateThreadLifecycleCaches(queryClient),
  });
};

export const useRestoreThreads = (): ReturnType<
  typeof useMutation<{ restored: number }, Error, { threadIds: string[] }>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadIds }) => {
      const { error, count } = await supabase
        .from('os_conversation_threads')
        .update({ deleted_at: null })
        .in('id', threadIds);

      if (error) throw error;

      return { restored: count ?? 0 };
    },
    onSuccess: () => invalidateThreadLifecycleCaches(queryClient),
  });
};

export const useBulkResolveThreads = (): ReturnType<
  typeof useMutation<{ resolved: number }, Error, { threadIds: string[] }>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadIds }) => {
      const { error, count } = await supabase
        .from('os_conversation_threads')
        .update({ status: 'resolved' })
        .in('id', threadIds);

      if (error) throw error;

      return { resolved: count ?? 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
    },
  });
};

/**
 * useDismissFollowUp — the operator's way to say "I have dealt with this, stop showing it".
 *
 * Resolve does NOT do this job and cannot: every deferred thread is ALREADY `resolved`, so the
 * existing bulk action rewrites the status it already has, reports "3 conversations resolved", and
 * leaves the row and the badge exactly where they were. The Follow-up tab is defined by a DATE, so
 * clearing the queue means clearing the date.
 *
 * The row count is read back and returned rather than assumed: PostgREST answers a predicate that
 * matches nothing with no error at all, and a dismiss that silently touched zero rows would be the
 * same false success in a new costume.
 */
export const useDismissFollowUp = (): ReturnType<
  typeof useMutation<{ dismissed: number }, Error, { threadIds: string[] }>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadIds }) => {
      if (threadIds.length === 0) return { dismissed: 0 };

      const { data, error } = await supabase
        .from('os_conversation_threads')
        .update({ follow_up_due_at: null })
        .in('id', threadIds)
        .select('id');

      if (error) throw error;

      const dismissed = data?.length ?? 0;
      if (dismissed !== threadIds.length) {
        throw new Error(
          `Dismissed ${dismissed} of ${threadIds.length} follow-ups — the rest were not changed.`
        );
      }
      return { dismissed };
    },
    onSuccess: () => invalidateThreadLifecycleCaches(queryClient),
  });
};

export const useBulkUpdateThreadStatus = (): ReturnType<
  typeof useMutation<
    { updated: number },
    Error,
    { threadIds: string[]; status: string }
  >
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ threadIds, status }) => {
      const { error, count } = await supabase
        .from('os_conversation_threads')
        .update({ status })
        .in('id', threadIds);

      if (error) throw error;

      return { updated: count ?? 0 };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation-threads'] });
      queryClient.invalidateQueries({ queryKey: ['thread-counts'] });
    },
  });
};
